import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CalculationHistory } from '@/components/CalculationHistory';
import { useCalculationHistory } from '@/hooks/useCalculationHistory';
import { Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const DiscountCalculator: React.FC = () => {
  const [originalPrice, setOriginalPrice] = useState<string>('');
  const [discountPercentage, setDiscountPercentage] = useState<string>('');
  const [result, setResult] = useState<{ finalPrice: number; amountSaved: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const { history, favorites, addCalculation, toggleFavorite, clearHistory } = useCalculationHistory();
  const { toast } = useToast();

  const calculate = (shouldAddToHistory = false) => {
    const original = parseFloat(originalPrice);
    const discount = parseFloat(discountPercentage);
    
    if (!isNaN(original) && !isNaN(discount) && original > 0 && discount >= 0) {
      const amountSaved = (discount / 100) * original;
      const finalPrice = original - amountSaved;
      const calculatedResult = { finalPrice, amountSaved };
      setResult(calculatedResult);
      
      // Add to history only when explicitly requested (like when user stops typing)
      if (shouldAddToHistory) {
        addCalculation(
          'discount',
          { originalPrice: original, discountPercentage: discount },
          `$${calculatedResult.finalPrice.toFixed(2)} (saved $${calculatedResult.amountSaved.toFixed(2)})`
        );
      }
    } else {
      setResult(null);
    }
  };

  // Real-time calculation as user types
  useEffect(() => {
    calculate();
  }, [originalPrice, discountPercentage]);

  // Add to history with debounce when user stops typing
  useEffect(() => {
    if (!originalPrice || !discountPercentage) return;
    
    const timer = setTimeout(() => {
      const original = parseFloat(originalPrice);
      const discount = parseFloat(discountPercentage);
      if (!isNaN(original) && !isNaN(discount) && original > 0 && discount >= 0) {
        calculate(true);
      }
    }, 1000); // Add to history 1 second after user stops typing

    return () => clearTimeout(timer);
  }, [originalPrice, discountPercentage]);

  const copyToClipboard = async () => {
    if (!result) return;
    
    const textToCopy = `Final Price: $${result.finalPrice.toFixed(2)} (saved $${result.amountSaved.toFixed(2)})`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast({
        title: "Copied to clipboard!",
        description: "The discount calculation result has been copied.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-card-foreground">
          Discount Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="originalPrice" className="text-sm font-medium">
              Original Price ($)
            </Label>
            <Input
              id="originalPrice"
              type="number"
              placeholder="Enter original price"
              value={originalPrice}
              onChange={(e) => setOriginalPrice(e.target.value)}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discountPercentage" className="text-sm font-medium">
              Discount Percentage (%)
            </Label>
            <Input
              id="discountPercentage"
              type="number"
              placeholder="Enter discount %"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              className="h-12"
            />
          </div>
        </div>
        
        {result !== null && (
          <div className="space-y-3">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg shadow-result">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Final Price:</p>
                  <p className="text-2xl font-bold text-primary">
                    ${result.finalPrice.toFixed(2)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyToClipboard}
                  className="text-muted-foreground hover:text-primary"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="p-4 bg-secondary border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Amount Saved:</p>
              <p className="text-xl font-semibold text-foreground">
                ${result.amountSaved.toFixed(2)}
              </p>
            </div>
          </div>
        )}
        
        <CalculationHistory
          history={history.filter(item => item.type === 'discount')}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onClearHistory={clearHistory}
        />
      </CardContent>
    </Card>
  );
};